// /opt/isp_erp/frontend/src/pages/ONUManagement.jsx

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  getUnauthorizedONUs,
  getONUTypes,
  createONUType,
  deleteONUType,
  getTrafficProfiles,
  createTrafficProfile,
  deleteTrafficProfile,
  authorizeONU,
  getAuthorizeStatus
} from '../api/olt.api';
import './ONUManagement.css';

export default function ONUManagement() {
  const { oltId } = useParams();
  const [activeTab, setActiveTab] = useState('unauthorized');
  const [loading, setLoading] = useState(false);
  const [authorizing, setAuthorizing] = useState(false);
  const [oltVendor, setOltVendor] = useState('');

  // Estados para cada sección
  const [unauthorizedONUs, setUnauthorizedONUs] = useState([]);
  const [onuTypes, setONUTypes] = useState([]);
  const [trafficProfiles, setTrafficProfiles] = useState([]);

  // Formularios
  const [newType, setNewType] = useState({
    name: '',
    eth_ports: 1,
    wifi: false,
    catv: false,
    vendor: 'huawei'
  });

  const [newProfile, setNewProfile] = useState({
    name: '',
    cir: 10240,
    direction: 'both',
    vendor: 'huawei'
  });

  const [authorizeData, setAuthorizeData] = useState({
    pon: '',
    sn: '',
    onu_type: '',
    line_profile: '',
    service_profile: '',
    vlan: 100,
    name: '',
    description: ''
  });

  // Cargar datos según la pestaña activa
  useEffect(() => {
    if (activeTab === 'unauthorized') loadUnauthorizedONUs();
    if (activeTab === 'types') loadONUTypes();
    if (activeTab === 'profiles') loadTrafficProfiles();
  }, [activeTab, oltId]);

  const loadUnauthorizedONUs = async () => {
    setLoading(true);
    try {
      const data = await getUnauthorizedONUs(oltId);
      setUnauthorizedONUs(data);
    } catch (error) {
      console.error('Error cargando ONUs no autorizadas:', error);
      alert('Error cargando ONUs no autorizadas');
    } finally {
      setLoading(false);
    }
  };

  const loadONUTypes = async () => {
    setLoading(true);
    try {
      const data = await getONUTypes(oltId);
      console.log('📊 Tipos de ONU recibidos:', data);
      console.log('📊 Cantidad de tipos:', data.length);
      setONUTypes(data);
      if (data.length > 0) {
        setOltVendor(data[0].vendor);
      }
    } catch (error) {
      console.error('Error cargando tipos de ONU:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTrafficProfiles = async () => {
    setLoading(true);
    try {
      const data = await getTrafficProfiles(oltId);
      console.log('📊 Perfiles de tráfico recibidos:', data);
      console.log('📊 Cantidad de perfiles:', data.length);
      console.log('📊 Primer perfil:', data[0]);
      console.log('📊 Segundo perfil:', data[1]);
      setTrafficProfiles(data);
    } catch (error) {
      console.error('Error cargando perfiles de tráfico:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateType = async (e) => {
    e.preventDefault();
    try {
      await createONUType(oltId, newType);
      alert('✅ Tipo de ONU creado exitosamente');
      setNewType({ name: '', eth_ports: 1, wifi: false, catv: false, vendor: oltVendor });
      loadONUTypes();
    } catch (error) {
      console.error('Error creando tipo:', error);
      alert('❌ Error creando tipo de ONU');
    }
  };

  const handleDeleteType = async (typeId) => {
    if (!window.confirm('¿Estás seguro de eliminar este tipo de ONU?')) return;
    try {
      await deleteONUType(oltId, typeId);
      alert('✅ Tipo de ONU eliminado');
      loadONUTypes();
    } catch (error) {
      console.error('Error eliminando tipo:', error);
      alert('❌ Error eliminando tipo de ONU');
    }
  };

  const handleCreateProfile = async (e) => {
    e.preventDefault();
    try {
      await createTrafficProfile(oltId, newProfile);
      alert('✅ Perfil de tráfico creado exitosamente');
      setNewProfile({ name: '', cir: 10240, direction: 'both', vendor: oltVendor });
      loadTrafficProfiles();
    } catch (error) {
      console.error('Error creando perfil:', error);
      alert('❌ Error creando perfil de tráfico');
    }
  };

  const handleDeleteProfile = async (profileId) => {
    if (!window.confirm('¿Estás seguro de eliminar este perfil de tráfico?')) return;
    try {
      await deleteTrafficProfile(oltId, profileId);
      alert('✅ Perfil de tráfico eliminado');
      loadTrafficProfiles();
    } catch (error) {
      console.error('Error eliminando perfil:', error);
      alert('❌ Error eliminando perfil de tráfico');
    }
  };

  // 👈 FUNCIÓN OPTIMIZADA - SIN RECARGAR DATOS INNECESARIOS
  const handleAuthorize = async (e) => {
    e.preventDefault();

    if (!authorizeData.pon || !authorizeData.sn || !authorizeData.onu_type ||
        !authorizeData.line_profile || !authorizeData.service_profile) {
      alert('❌ Por favor complete todos los campos requeridos');
      return;
    }

    setAuthorizing(true);

    try {
      const data = {
        olt_id: parseInt(oltId),
        pon: authorizeData.pon,
        sn: authorizeData.sn,
        onu_type: authorizeData.onu_type,
        line_profile: authorizeData.line_profile,
        service_profile: authorizeData.service_profile,
        vlan: parseInt(authorizeData.vlan),
        name: authorizeData.name || authorizeData.sn,
        description: authorizeData.description
      };

      // 1. Iniciar autorización DIRECTAMENTE (sin recargar datos previos)
      const response = await authorizeONU(data);
      const { task_id } = response;

      if (!task_id) {
        throw new Error('No se recibió task_id');
      }

      // 2. Mostrar overlay de espera
      const overlay = document.createElement('div');
      overlay.className = 'loading-overlay';
      overlay.id = 'auth-loading-overlay';
      overlay.innerHTML = `
        <div class="loading-content">
          <div class="spinner-large"></div>
          <p style={{ fontSize: '18px', fontWeight: 'bold' }}>⏳ Autorizando ONU...</p>
          <p style={{ color: '#666', marginTop: '10px' }}>Este proceso puede tomar hasta 5 minutos</p>
          <p style={{ color: '#999', fontSize: '13px', marginTop: '5px' }}>No cierre esta ventana</p>
        </div>
      `;
      document.body.appendChild(overlay);

      // 3. Polling para consultar estado
      let completed = false;
      let attempts = 0;
      const maxAttempts = 100;

      while (!completed && attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 3000));
        attempts++;

        try {
          const statusData = await getAuthorizeStatus(task_id);

          if (statusData.status === 'completed') {
            completed = true;
            const overlayEl = document.getElementById('auth-loading-overlay');
            if (overlayEl) document.body.removeChild(overlayEl);

            if (statusData.result && statusData.result.success) {
              alert(`✅ ONU autorizada exitosamente: ${statusData.result.interface || authorizeData.pon + ':' + statusData.result.onu_id}`);
              // Limpiar formulario
              setAuthorizeData({
                pon: '', sn: '', onu_type: '', line_profile: '',
                service_profile: '', vlan: 100, name: '', description: ''
              });
              // ✅ SOLO RECARGAR ONUs NO AUTORIZADAS si estamos en esa pestaña
              if (activeTab === 'unauthorized') {
                await loadUnauthorizedONUs();
              }
            } else {
              const errorMsg = statusData.result?.error || 'Error desconocido';
              alert(`❌ Error autorizando ONU: ${errorMsg}`);
            }
          } else if (statusData.status === 'failed') {
            completed = true;
            const overlayEl = document.getElementById('auth-loading-overlay');
            if (overlayEl) document.body.removeChild(overlayEl);
            const errorMsg = statusData.result?.error || 'Error desconocido';
            alert(`❌ Error autorizando ONU: ${errorMsg}`);
          }
          // Si status es 'queued' o 'processing', continuar esperando

        } catch (pollError) {
          console.error('Error consultando estado:', pollError);
          // Continuar intentando
        }
      }

      if (!completed) {
        const overlayEl = document.getElementById('auth-loading-overlay');
        if (overlayEl) document.body.removeChild(overlayEl);
        alert('⏰ La autorización está tomando demasiado tiempo. Verifique el estado manualmente.');
      }

    } catch (error) {
      console.error('Error autorizando ONU:', error);
      const overlayEl = document.getElementById('auth-loading-overlay');
      if (overlayEl) document.body.removeChild(overlayEl);
      const errorMsg = error.response?.data?.detail || error.message || 'Error desconocido';
      alert(`❌ Error autorizando ONU: ${errorMsg}`);
    } finally {
      setAuthorizing(false);
    }
  };

  const fillFromUnauthorized = (onu) => {
    setAuthorizeData({
      ...authorizeData,
      pon: onu.pon,
      sn: onu.sn,
      name: onu.sn
    });
    setActiveTab('authorize');
  };

  return (
    <div className="onu-management">
      <h2>Gestión de ONUs - OLT #{oltId}</h2>

      <div className="tabs">
        <button
          className={activeTab === 'unauthorized' ? 'active' : ''}
          onClick={() => setActiveTab('unauthorized')}
        >
          📋 ONUs No Autorizadas ({unauthorizedONUs.length})
        </button>
        <button
          className={activeTab === 'types' ? 'active' : ''}
          onClick={() => setActiveTab('types')}
        >
          📦 Tipos de ONU ({onuTypes.length})
        </button>
        <button
          className={activeTab === 'profiles' ? 'active' : ''}
          onClick={() => setActiveTab('profiles')}
        >
          📊 Perfiles de Tráfico ({trafficProfiles.length})
        </button>
        <button
          className={activeTab === 'authorize' ? 'active' : ''}
          onClick={() => setActiveTab('authorize')}
        >
          ✍️ Autorizar ONU
        </button>
      </div>

      <div className="tab-content">
        {/* TAB 1: ONUs No Autorizadas */}
        {activeTab === 'unauthorized' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h3>ONUs No Autorizadas</h3>
              <button
                onClick={loadUnauthorizedONUs}
                className="btn-refresh"
                disabled={loading}
              >
                🔄 {loading ? 'Cargando...' : 'Actualizar'}
              </button>
            </div>

            {loading ? (
              <div className="loading">Cargando...</div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>PON</th>
                    <th>Serial Number</th>
                    <th>ONU ID</th>
                    <th>Interfaz</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {unauthorizedONUs.map((onu, idx) => (
                    <tr key={idx}>
                      <td><code>{onu.pon}</code></td>
                      <td><code className="sn-code">{onu.sn}</code></td>
                      <td>{onu.onu_id}</td>
                      <td><code>{onu.interface}</code></td>
                      <td>
                        <button
                          className="btn-authorize-small"
                          onClick={() => fillFromUnauthorized(onu)}
                        >
                          Autorizar
                        </button>
                      </td>
                    </tr>
                  ))}
                  {unauthorizedONUs.length === 0 && (
                    <tr>
                      <td colSpan="5" className="empty-state">
                        No hay ONUs no autorizadas
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* TAB 2: Tipos de ONU */}
        {activeTab === 'types' && (
          <div className="two-columns">
            <div className="form-section">
              <h3>Crear Nuevo Tipo de ONU</h3>
              <form onSubmit={handleCreateType}>
                <div className="form-group">
                  <label>Nombre del Tipo:</label>
                  <input
                    type="text"
                    value={newType.name}
                    onChange={(e) => setNewType({...newType, name: e.target.value})}
                    placeholder="Ej: HG8010, F601"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Puertos Ethernet:</label>
                  <input
                    type="number"
                    min="1"
                    max="4"
                    value={newType.eth_ports}
                    onChange={(e) => setNewType({...newType, eth_ports: parseInt(e.target.value)})}
                  />
                </div>

                <div className="form-group checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={newType.wifi}
                      onChange={(e) => setNewType({...newType, wifi: e.target.checked})}
                    />
                    Tiene WiFi
                  </label>
                </div>

                <div className="form-group checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={newType.catv}
                      onChange={(e) => setNewType({...newType, catv: e.target.checked})}
                    />
                    Tiene CATV (RF)
                  </label>
                </div>

                <button type="submit" className="btn-primary">
                  Crear Tipo de ONU
                </button>
              </form>
            </div>

            <div className="list-section">
              <h3>Tipos Existentes</h3>
              <table className="table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Nombre</th>
                    <th>Puertos</th>
                    <th>WiFi</th>
                    <th>CATV</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {onuTypes.map((type) => (
                    <tr key={type.id}>
                      <td>{type.id}</td>
                      <td>{type.name}</td>
                      <td>{type.eth_ports || type.ports || 1}</td>
                      <td>{type.wifi ? '✅' : '❌'}</td>
                      <td>{type.catv ? '✅' : '❌'}</td>
                      <td>
                        <button
                          className="btn-delete-small"
                          onClick={() => handleDeleteType(type.id)}
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB 3: Perfiles de Tráfico */}
        {activeTab === 'profiles' && (
          <div className="two-columns">
            <div className="form-section">
              <h3>Crear Perfil de Tráfico</h3>
              <form onSubmit={handleCreateProfile}>
                <div className="form-group">
                  <label>Nombre del Perfil:</label>
                  <input
                    type="text"
                    value={newProfile.name}
                    onChange={(e) => setNewProfile({...newProfile, name: e.target.value})}
                    placeholder="Ej: 10M, 20M"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Velocidad (CIR en kbps):</label>
                  <input
                    type="number"
                    min="64"
                    value={newProfile.cir}
                    onChange={(e) => setNewProfile({...newProfile, cir: parseInt(e.target.value)})}
                    required
                  />
                  <small>Ej: 1024 = 1Mbps, 10240 = 10Mbps</small>
                </div>

                <div className="form-group">
                  <label>Dirección:</label>
                  <select
                    value={newProfile.direction}
                    onChange={(e) => setNewProfile({...newProfile, direction: e.target.value})}
                  >
                    <option value="both">Ambos (Up & Down)</option>
                    <option value="upstream">Solo Upstream</option>
                    <option value="downstream">Solo Downstream</option>
                  </select>
                </div>

                <button type="submit" className="btn-primary">
                  Crear Perfil
                </button>
              </form>
            </div>

            <div className="list-section">
              <h3>Perfiles Existentes</h3>
              <table className="table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Nombre</th>
                    <th>Dirección</th>
                    <th>Velocidad</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {trafficProfiles.map((profile) => (
                    <tr key={profile.id}>
                      <td>{profile.id}</td>
                      <td>{profile.name}</td>
                      <td>
                        {profile.direction === 'upstream' ? '⬆️ Upstream' :
                         profile.direction === 'downstream' ? '⬇️ Downstream' : '🔄 Ambos'}
                      </td>
                      <td>{(profile.cir || profile.bandwidth || 0) / 1024} Mbps</td>
                      <td>
                        <button
                          className="btn-delete-small"
                          onClick={() => handleDeleteProfile(profile.id)}
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB 4: Autorizar ONU */}
        {activeTab === 'authorize' && (
          <div className="authorize-form">
            <h3>Autorizar Nueva ONU</h3>

            <form onSubmit={handleAuthorize}>
              <div className="form-row">
                <div className="form-group">
                  <label>PON (Frame/Slot/Port):</label>
                  <input
                    type="text"
                    value={authorizeData.pon}
                    onChange={(e) => setAuthorizeData({...authorizeData, pon: e.target.value})}
                    placeholder="Ej: 1/1/7"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Serial Number:</label>
                  <input
                    type="text"
                    value={authorizeData.sn}
                    onChange={(e) => setAuthorizeData({...authorizeData, sn: e.target.value})}
                    placeholder="Ej: ZTEGC12345678"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Tipo de ONU:</label>
                  <select
                    value={authorizeData.onu_type}
                    onChange={(e) => setAuthorizeData({...authorizeData, onu_type: e.target.value})}
                    required
                  >
                    <option value="">Seleccione tipo</option>
                    {onuTypes.map(type => (
                      <option key={type.id} value={type.name}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Perfil Upstream (TCONT):</label>
                  <select
                    value={authorizeData.line_profile}
                    onChange={(e) => setAuthorizeData({...authorizeData, line_profile: e.target.value})}
                    required
                  >
                    <option value="">Seleccione perfil</option>
                    {trafficProfiles
                      .filter(p => p.type === 'TCONT' || p.direction === 'upstream')
                      .map(profile => (
                        <option key={profile.id} value={profile.name || profile.id}>
                          {profile.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Perfil Downstream (TRAFFIC):</label>
                  <select
                    value={authorizeData.service_profile}
                    onChange={(e) => setAuthorizeData({...authorizeData, service_profile: e.target.value})}
                    required
                  >
                    <option value="">Seleccione perfil</option>
                    {trafficProfiles
                      .filter(p => p.type === 'TRAFFIC' || p.direction === 'downstream')
                      .map(profile => (
                        <option key={profile.id} value={profile.name || profile.id}>
                          {profile.name}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>VLAN:</label>
                  <input
                    type="number"
                    min="1"
                    max="4094"
                    value={authorizeData.vlan}
                    onChange={(e) => setAuthorizeData({...authorizeData, vlan: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Nombre del Cliente:</label>
                <input
                  type="text"
                  value={authorizeData.name}
                  onChange={(e) => setAuthorizeData({...authorizeData, name: e.target.value})}
                  placeholder="Ej: Juan Pérez"
                  required
                />
              </div>

              <div className="form-group">
                <label>Descripción (opcional):</label>
                <textarea
                  value={authorizeData.description}
                  onChange={(e) => setAuthorizeData({...authorizeData, description: e.target.value})}
                  rows="2"
                  placeholder="Dirección u observaciones"
                />
              </div>

              <button
                type="submit"
                className="btn-success"
                disabled={authorizing}
              >
                {authorizing ? (
                  <>
                    <span className="spinner"></span>
                    Autorizando ONU...
                  </>
                ) : (
                  'Autorizar ONU'
                )}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

